import { useCallback, useEffect, useState } from 'react'
import {
  createCodes,
  deleteCode,
  listCodes,
  revokeCode,
  type ActivationCode,
} from '../../services/activation'
import { type FeatureKey, type TenantType } from '../../types'
import { Spinner } from '../Spinner'
import { KeyRoundIcon } from '../icons'
import { FeatureTable } from './FeatureTable'

/** Turkish date, no time: a code's life is measured in days. */
const asDate = (iso: string) =>
  new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' })

const STATUS_LABEL: Record<ActivationCode['status'], string> = {
  active: 'Bekliyor',
  used: 'Kullanıldı',
  revoked: 'İptal',
}

/**
 * Issuing activation codes, and seeing what has become of the ones already
 * issued.
 *
 * The codes themselves are shown once, on the screen that made them, and are
 * not recoverable afterwards, only their hashes are stored. That is the
 * whole point of storing them that way, so the panel says so plainly rather
 * than leaving someone hunting for a copy button that cannot exist.
 *
 * Issuing is behind PANEL_SECRET like deleting an institution is. A code is
 * an open door to a new institution, which is the same class of act as
 * closing an existing one.
 */
export function CodesPanel() {
  const [codes, setCodes] = useState<ActivationCode[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [count, setCount] = useState('1')
  const [days, setDays] = useState('30')
  const [note, setNote] = useState('')
  const tenantType: TenantType = 'restaurant'
  const [features, setFeatures] = useState<Record<string, boolean>>({})

  const [busy, setBusy] = useState(false)
  /** The plain codes, held only until this screen is left. */
  const [issued, setIssued] = useState<string[] | null>(null)
  /** Set for a moment after copying, so the button can say it worked. */
  const [copied, setCopied] = useState(false)

  /**
   * Copies the codes and says so.
   *
   * The button used to fire and look identical afterwards, which reads as a
   * button that does nothing, and these are codes that cannot be recovered,
   * so "did that work?" is the worst question to leave someone with. The
   * fallback matters for the same reason: clipboard access is refused in
   * enough contexts that a silent failure here would lose the codes.
   */
  async function onCopy(text: string) {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setError('Kopyalanamadı. Kodları elle seçip kopyalayın.')
    }
  }

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setCodes(await listCodes())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kodlar yüklenemedi.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  /**
   * No PanelGate in front of this, unlike closing an institution.
   *
   * That gate carries PANEL_SECRET to functions/admin, which checks it.
   * Issuing goes straight through RLS, so a gate here would be a password box
   * nothing validates, theatre, and the worst kind, because it would read as
   * protection while adding none. What actually guards this row is the RLS
   * policy: only the system administrator can write activation_codes.
   *
   * An unwanted code is also cheap to undo, revoke it, which is not true of
   * the actions the gate exists for.
   */
  async function onIssue() {
    setBusy(true)
    setError(null)
    try {
      const made = await createCodes({
        count: Number(count) || 1,
        days: Number(days) || 30,
        note,
        tenantType,
        features: features as Record<FeatureKey, boolean>,
      })
      setIssued(made)
      setNote('')
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kod üretilemedi.')
    } finally {
      setBusy(false)
    }
  }

  async function onRevoke(code: ActivationCode) {
    try {
      await revokeCode(code.id)
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'İptal edilemedi.')
    }
  }

  async function onDelete(code: ActivationCode) {
    try {
      await deleteCode(code.id)
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Silinemedi.')
    }
  }

  return (
    <div className="codes">
      <div className="codes__new">
        {/* "Kod üret" başlığın sağında (1 Kas 2026, kullanıcı). */}
        <div className="codes__head">
          <h3>Yeni aktivasyon kodu</h3>
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy || !count}
            onClick={() => void onIssue()}
          >
            <KeyRoundIcon />
            {busy ? 'Üretiliyor…' : 'Kod üret'}
          </button>
        </div>

        <div className="codes__row">
          <label>
            Adet
            <input
              value={count}
              onChange={(e) => setCount(e.target.value.replace(/\D/g, ''))}
              inputMode="numeric"
            />
          </label>
          <label>
            Geçerlilik (gün)
            <input
              value={days}
              onChange={(e) => setDays(e.target.value.replace(/\D/g, ''))}
              inputMode="numeric"
            />
          </label>
          {/* Aynı satırda (1 Kas 2026, kullanıcı: "yatay olsun"). */}
          <label className="codes__note">
            Kim için
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Denizli, Ahmet Bey"
            />
          </label>
        </div>

        <FeatureTable label="Açık gelecek özellikler" features={features} onChange={setFeatures} />

      </div>

      {/* The one moment these exist in readable form. Saying so is not a
          warning for its own sake: there is no second chance to copy them. */}
      {issued && (
        <div className="codes__issued">
          <h4>Üretilen kodlar</h4>
          <p className="superadmin__hint">
            Bu kodlar yalnızca burada görünüyor; kapatınca bir daha
            gösterilemez. Şimdi kopyalayın.
          </p>
          <pre className="codes__issued-list">{issued.join('\n')}</pre>
          <div className="codes__issued-buttons">
            <button
              type="button"
              className="btn btn--outline"
              onClick={() => void onCopy(issued.join('\n'))}
            >
              {copied ? 'Kopyalandı ✓' : 'Kopyala'}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setIssued(null)
                setCopied(false)
              }}
            >
              Kapat
            </button>
          </div>
        </div>
      )}

      {error && <p className="superadmin__error">{error}</p>}

      <h3 className="codes__list-head">Kodlar</h3>
      {/* Said once, above the list, rather than left to be discovered by a
          code that will not work. */}
      <p className="superadmin__hint codes__list-note">
        Aşağıdakiler kısaltılmış etiketlerdir, kodun kendisi değildir. Kod
        yalnızca üretildiği anda görünür; kaybolan bir kod yenilenir.
      </p>
      {loading ? (
        <Spinner label="Yükleniyor…" />
      ) : codes.length === 0 ? (
        <p className="superadmin__empty">Henüz kod üretilmedi.</p>
      ) : (
        <ul className="codes__list">
          {codes.map((c) => {
            const expired = c.status === 'active' && new Date(c.expiresAt).getTime() < Date.now()
            return (
              <li key={c.id} className={`codes__item is-${expired ? 'expired' : c.status}`}>
                <div className="codes__item-main">
                  {/* Titled, because this is the one string here someone might
                      reasonably try to use as the code, and it never can be. */}
                  <span
                    className="codes__label"
                    title="Kısaltılmış etiket. Kodun tamamı yalnızca üretildiği anda gösterilir."
                  >
                    {c.label}
                  </span>
                  {c.note && <span className="codes__item-note">{c.note}</span>}
                </div>
                <div className="codes__item-meta">
                  <span className="codes__status">
                    {expired ? 'Süresi doldu' : STATUS_LABEL[c.status]}
                  </span>
                  {c.status === 'used' && c.usedByTenant && (
                    <span className="codes__used">{c.usedByTenant}</span>
                  )}
                  {c.status === 'active' && !expired && (
                    <span className="codes__expiry">{asDate(c.expiresAt)}'e kadar</span>
                  )}
                </div>
                <div className="codes__item-actions">
                  {c.status === 'active' && !expired && (
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => void onRevoke(c)}
                    >
                      İptal et
                    </button>
                  )}
                  {(c.status !== 'active' || expired) && (
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => void onDelete(c)}
                    >
                      Sil
                    </button>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
